import { useQuery, useQueryClient } from '@tanstack/react-query';
import { History, SlidersHorizontal } from 'lucide-react';
import { useId, useRef, useState, type JSX } from 'react';

import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';

import { ModuleBulkDialog } from './ModuleBulkDialog';
import { ModuleHistoryPanel } from './ModuleHistoryPanel';
import { ModuleToggleDialog } from './ModuleToggleDialog';
import styles from './TenantModulesTab.module.scss';
import {
  getTenantModules,
  tenantModulesQueryKey,
  type TenantModule,
} from '../api/tenant-modules.graphql';
import { useModulePresets } from '../hooks/useModuleCatalog';
import { formatDate, moduleNames, sourceLabel } from '../module-labels';

interface Props {
  tenantId: string;
}

/** Por qué no se puede cambiar un módulo, con los datos que calculó el API. */
function blockReason(module: TenantModule, names: ReadonlyMap<string, string>): string | null {
  if (module.isCore) {
    return 'Módulo base: siempre habilitado.';
  }
  if (module.enabled && module.requiredBy.length > 0) {
    return `No se puede deshabilitar: lo usan ${moduleNames(module.requiredBy, names)}. Deshabilítalos primero.`;
  }
  if (!module.enabled && module.missingDependencies.length > 0) {
    return `Para habilitarlo, primero habilita: ${moduleNames(module.missingDependencies, names)}.`;
  }
  return null;
}

function statusText(module: TenantModule): string {
  if (module.enabled) {
    return module.enabledAt ? `Habilitado desde el ${formatDate(module.enabledAt)}` : 'Habilitado';
  }
  return module.disabledAt
    ? `Deshabilitado desde el ${formatDate(module.disabledAt)}`
    : 'No incluido';
}

/**
 * OS-1716 — pestaña "Módulos" de la ficha del tenant: estado, dependencias, cambios con motivo y
 * su historial. Lo que se puede cambiar lo decide el API (`requiredBy`, `missingDependencies`,
 * `isCore`); aquí solo se presenta. OS-1717: sin `platform.modules.write` la pestaña es de solo
 * lectura y sin `platform.audit.read` no muestra el historial.
 */
export function TenantModulesTab({ tenantId }: Props): JSX.Element {
  const qc = useQueryClient();
  const idPrefix = useId();
  const historyRef = useRef<HTMLHeadingElement>(null);
  const [toggling, setToggling] = useState<TenantModule | null>(null);
  const [bulk, setBulk] = useState(false);
  const [historyModule, setHistoryModule] = useState<string | null>(null);
  const [notice, setNotice] = useState('');

  // OS-1717 — cambios con `platform.modules.write`; historial con `platform.audit.read`.
  const { can } = usePlatformPermissions();
  const canWrite = can('platform.modules.write');
  const canReadHistory = can('platform.audit.read');
  // OS-1723 — impacto en la suscripción al cambiar un módulo.
  const canReadBilling = can('platform.billing.read');
  const canWriteBilling = can('platform.billing.write');

  const modules = useQuery({
    queryKey: tenantModulesQueryKey(tenantId),
    queryFn: () => getTenantModules(tenantId),
  });
  const presets = useModulePresets();
  const presetNames = new Map((presets.data ?? []).map((preset) => [preset.key, preset.name]));

  function applied(next: TenantModule[], message: string): void {
    qc.setQueryData(tenantModulesQueryKey(tenantId), next);
    void qc.invalidateQueries({ queryKey: ['platform-tenant-module-history', tenantId] });
    // OS-1718 — el paso "Módulos contratados" del checklist (y el listado) se recalculan.
    void qc.invalidateQueries({ queryKey: ['platform-tenants'] });
    // OS-1723 — el MRR del dashboard cambia si se cerró o agregó una línea de suscripción.
    void qc.invalidateQueries({ queryKey: ['platform-summary'] });
    setToggling(null);
    setBulk(false);
    setNotice(`${message} El cambio quedó auditado.`);
  }

  function rejected(): void {
    // Otro administrador pudo cambiar los módulos: se recarga el estado sin cerrar el diálogo.
    void qc.invalidateQueries({ queryKey: tenantModulesQueryKey(tenantId) });
  }

  function showHistory(moduleKey: string): void {
    setHistoryModule(moduleKey);
    historyRef.current?.focus();
    historyRef.current?.scrollIntoView?.({ block: 'start' });
  }

  if (modules.isLoading) {
    return <p className={styles.muted} role="status">Cargando módulos…</p>;
  }
  if (modules.isError) {
    return (
      <div className={styles.errorBox} role="alert">
        <span>No se pudieron cargar los módulos: {modules.error.message}</span>
        <button type="button" className={styles.linkBtn} onClick={() => void modules.refetch()}>
          Reintentar
        </button>
      </div>
    );
  }
  const list = modules.data ?? [];
  if (list.length === 0) {
    return <p className={styles.muted}>El catálogo de módulos está vacío: revisa el despliegue del API.</p>;
  }
  const names = new Map(list.map((m) => [m.key, m.name]));
  const enabledCount = list.filter((m) => m.enabled).length;

  return (
    <div className={styles.stack}>
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2 className={styles.panelTitle}>Módulos del tenant</h2>
            <p className={styles.muted}>
              {enabledCount} de {list.length} módulos habilitados.{' '}
              {canWrite
                ? 'Cada cambio pide un motivo y queda en la auditoría.'
                : 'Solo lectura: tu rol no cambia los módulos del tenant.'}
            </p>
            <p className={styles.muted}>
              El acceso del tenant solo se bloquea si el control de módulos está activo en el
              ambiente; mientras no lo esté, los cambios se guardan y auditan sin efecto en las apps.
            </p>
          </div>
          {canWrite ? (
            <button type="button" className={styles.secondaryBtn} onClick={() => setBulk(true)}>
              <SlidersHorizontal size={15} aria-hidden="true" />
              Cambiar varios o aplicar preset
            </button>
          ) : null}
        </div>
        <p className={styles.notice} role="status">
          {notice}
        </p>

        <ul className={styles.grid}>
          {list.map((module) => {
            const titleId = `${idPrefix}-${module.key}-title`;
            const reasonId = `${idPrefix}-${module.key}-reason`;
            const blocked = canWrite ? blockReason(module, names) : null;
            const source = sourceLabel(module.source, presetNames);
            return (
              <li key={module.key} className={module.enabled ? styles.card : styles.cardOff}>
                <div className={styles.cardHeader}>
                  <h3 id={titleId} className={styles.cardTitle}>
                    {module.name}
                    {module.isCore ? <span className={styles.badge}>Base</span> : null}
                  </h3>
                  {canWrite ? (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={module.enabled}
                    aria-labelledby={titleId}
                    aria-describedby={blocked ? reasonId : undefined}
                    aria-disabled={blocked ? true : undefined}
                    className={styles.switch}
                    onClick={() => {
                      if (!blocked) {
                        setNotice('');
                        setToggling(module);
                      }
                    }}
                  >
                    <span className={styles.thumb} aria-hidden="true" />
                  </button>
                  ) : (
                    <span className={module.enabled ? styles.on : styles.off}>
                      {module.enabled ? 'Habilitado' : 'No incluido'}
                    </span>
                  )}
                </div>
                {module.description ? <p className={styles.description}>{module.description}</p> : null}
                <p className={module.enabled ? styles.on : styles.off}>
                  {statusText(module)}
                  {source ? <span className={styles.muted}> · {source}</span> : null}
                </p>
                {module.dependsOn.length > 0 ? (
                  <p className={styles.muted}>Requiere: {moduleNames(module.dependsOn, names)}</p>
                ) : null}
                {blocked ? (
                  <p id={reasonId} className={module.isCore ? styles.muted : styles.blocked}>
                    {blocked}
                  </p>
                ) : null}
                {!module.isCore && canReadHistory ? (
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => showHistory(module.key)}
                  >
                    <History size={13} aria-hidden="true" />
                    Ver historial
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>

      {canReadHistory ? (
      <div className={styles.panel}>
        <ModuleHistoryPanel
          ref={historyRef}
          tenantId={tenantId}
          modules={list.filter((m) => !m.isCore)}
          presetNames={presetNames}
          moduleKey={historyModule}
          onModuleKeyChange={setHistoryModule}
        />
      </div>
      ) : null}

      {toggling && canWrite ? (
        <ModuleToggleDialog
          tenantId={tenantId}
          module={toggling}
          modules={list}
          onDone={(next, note) =>
            applied(
              next,
              `${toggling.name} ${toggling.enabled ? 'deshabilitado' : 'habilitado'}.` +
                (note ? ` ${note}` : ''),
            )
          }
          canReadBilling={canReadBilling}
          canWriteBilling={canWriteBilling}
          onRejected={rejected}
          onCancel={() => setToggling(null)}
        />
      ) : null}
      {bulk && canWrite ? (
        <ModuleBulkDialog
          tenantId={tenantId}
          modules={list}
          onDone={(next) => applied(next, 'Módulos actualizados.')}
          canReadBilling={canReadBilling}
          onRejected={rejected}
          onCancel={() => setBulk(false)}
        />
      ) : null}
    </div>
  );
}
